/** localStorage key for release banner page-load dedupe (legacy: `true`; CC-7074: latest tracker version string). */
export const RELEASE_VERSION_VERIFIED_STORAGE_KEY = 'releaseVersionVerified'

/** Must match router default `ROUTER_PIN_COOKIE_NAME`. */
export const SPA_VERSION_PINNED_COOKIE_NAME = 'spa_version_pinned'

export const ROUTER_UPGRADE_URL = '/router/upgrade'
export const ROUTER_DEBUG_URL = '/router/debug'

export function readReleaseVersionVerifiedStorage() {
  try {
    const raw = localStorage?.getItem(RELEASE_VERSION_VERIFIED_STORAGE_KEY)
    if (raw == null) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * @param {string} [cookieHeader]
 * @returns {boolean} Whether `spa_version_pinned` is set to true in the cookie header.
 */
export function readSpaVersionPinnedFromCookie(
  cookieHeader = typeof document !== 'undefined' ? document.cookie : '',
) {
  if (!cookieHeader) return false
  const segments = cookieHeader.split(';')
  for (const segment of segments) {
    const trimmed = segment.trim()
    if (!trimmed.startsWith(`${SPA_VERSION_PINNED_COOKIE_NAME}=`)) continue
    const value = trimmed
      .slice(SPA_VERSION_PINNED_COOKIE_NAME.length + 1)
      .trim()
      .toLowerCase()
    return value === 'true'
  }
  return false
}

/**
 * Maps `VITE_ENVIRONMENT` to `release_version_tracker.environment` values.
 * @param {string | null | undefined} viteEnvironment
 */
export function resolveReleaseTrackerEnvironment(viteEnvironment) {
  const raw = String(viteEnvironment ?? '')
    .trim()
    .toLowerCase()
  if (raw === 'dev') return 'development'
  if (raw === 'qa') return 'staging'
  if (raw === 'prod') return 'production'
  return raw
}

export function normalizeReleaseVersion(value) {
  if (value == null) return null
  const trimmed = String(value).trim().replace(/^v/i, '')
  return trimmed.length > 0 ? trimmed : null
}

/**
 * @param {string | null | undefined} version
 * @returns {number[] | null} Numeric semver segments, or null when invalid.
 */
function parseSemverParts(version) {
  const normalized = normalizeReleaseVersion(version)
  if (!normalized) return null
  const parts = normalized.split('.')
  if (parts.length < 2 || parts.length > 4) return null
  const numeric = parts.map((part) => Number.parseInt(part, 10))
  if (numeric.some((part) => Number.isNaN(part))) return null
  return numeric
}

/**
 * @param {string | null | undefined} left
 * @param {string | null | undefined} right
 * @returns {number} negative if left < right, positive if left > right, 0 if equal
 */
export function compareReleaseVersions(left, right) {
  const leftParts = parseSemverParts(left)
  const rightParts = parseSemverParts(right)
  if (!leftParts || !rightParts) return 0
  const length = Math.max(leftParts.length, rightParts.length, 3)
  for (let index = 0; index < length; index += 1) {
    const diff = (leftParts[index] ?? 0) - (rightParts[index] ?? 0)
    if (diff !== 0) return diff
  }
  return 0
}

/**
 * CC-7266 / FE-3039: with `hotfixReleasePopupFixEnabled`, unpinned users are only behind on a
 * major/minor change (hotfix/patch releases are ignored); pinned users still compare full semver.
 * @param {string | null | undefined} buildVersion
 * @param {string | null | undefined} releaseVersion
 * @param {{ spaVersionPinned?: boolean, hotfixReleasePopupFixEnabled?: boolean }} [options]
 */
export function isBuildBehindTrackedRelease(
  buildVersion,
  releaseVersion,
  { spaVersionPinned = false, hotfixReleasePopupFixEnabled = false } = {},
) {
  const buildParts = parseSemverParts(buildVersion)
  const releaseParts = parseSemverParts(releaseVersion)
  if (!buildParts || !releaseParts) {
    return false
  }

  const majorMinorDiffers = buildParts[0] !== releaseParts[0] || buildParts[1] !== releaseParts[1]

  if (hotfixReleasePopupFixEnabled && !spaVersionPinned) {
    // Only a strictly older major.minor counts; a build ahead of a lagging tracker is not behind.
    return (
      buildParts[0] < releaseParts[0] ||
      (buildParts[0] === releaseParts[0] && buildParts[1] < releaseParts[1])
    )
  }

  if (releaseParts.length === 2) {
    return majorMinorDiffers
  }

  return compareReleaseVersions(buildVersion, releaseVersion) !== 0
}

/**
 * Whether to show the release upgrade banner (distinct from router pin state).
 * @param {string | null | undefined} buildVersion
 * @param {string | null | undefined} releaseVersion
 * @param {{ defaultShellVersion?: string | null, spaVersionPinned?: boolean, hotfixReleasePopupFixEnabled?: boolean }} [options]
 */
export function shouldShowReleaseVersionBanner(
  buildVersion,
  releaseVersion,
  {
    defaultShellVersion = null,
    spaVersionPinned = false,
    hotfixReleasePopupFixEnabled = false,
  } = {},
) {
  if (
    !isBuildBehindTrackedRelease(buildVersion, releaseVersion, {
      spaVersionPinned,
      hotfixReleasePopupFixEnabled,
    })
  ) {
    return false
  }

  const build = normalizeReleaseVersion(buildVersion)
  const release = normalizeReleaseVersion(releaseVersion)
  const defaultShell = normalizeReleaseVersion(defaultShellVersion)

  if (release && defaultShell && compareReleaseVersions(release, defaultShell) > 0) {
    return false
  }

  if (!spaVersionPinned && build && defaultShell && build === defaultShell) {
    return false
  }

  return true
}

/**
 * @param {unknown} stored
 * @param {string | null | undefined} latestReleaseVersion
 * @param {string | null | undefined} [buildVersion]
 * @param {{ defaultShellVersion?: string | null, hotfixReleasePopupFixEnabled?: boolean }} [routerContext]
 * @param {boolean} [spaVersionPinned]
 */
export function shouldSkipReleaseVersionPageCheck(
  stored,
  latestReleaseVersion,
  buildVersion = null,
  routerContext = {},
  spaVersionPinned = false,
) {
  if (!latestReleaseVersion) {
    return false
  }

  if (
    shouldShowReleaseVersionBanner(buildVersion, latestReleaseVersion, {
      ...routerContext,
      spaVersionPinned,
    })
  ) {
    return false
  }

  return typeof stored === 'string' && stored === latestReleaseVersion
}

/**
 * @param {string | null | undefined} latestReleaseVersion
 * @param {string | null | undefined} [buildVersion]
 * @param {{ defaultShellVersion?: string | null, hotfixReleasePopupFixEnabled?: boolean }} [routerContext]
 * @param {boolean} [spaVersionPinned]
 */
export function persistReleaseVersionPageCheck(
  latestReleaseVersion,
  buildVersion = null,
  routerContext = {},
  spaVersionPinned = false,
) {
  if (
    shouldShowReleaseVersionBanner(buildVersion, latestReleaseVersion, {
      ...routerContext,
      spaVersionPinned,
    })
  ) {
    return
  }
  localStorage.setItem(
    RELEASE_VERSION_VERIFIED_STORAGE_KEY,
    JSON.stringify(latestReleaseVersion ?? ''),
  )
}

export async function fetchRouterReleaseContext() {
  try {
    const response = await fetch(ROUTER_DEBUG_URL, { credentials: 'same-origin' })
    if (!response.ok) {
      return { defaultShellVersion: null }
    }
    const data = await response.json()
    return {
      defaultShellVersion: data.defaultShellVersion ?? null,
    }
  } catch {
    return { defaultShellVersion: null }
  }
}

export async function postRouterUpgrade() {
  return fetch(ROUTER_UPGRADE_URL, {
    method: 'POST',
    credentials: 'same-origin',
  })
}
